// banner-bus.ts — helper-functions zum Dispatchen von pfs:banner-Events
// an <pfs-banner-host>. Konsumer (apiFetch + Cut-App-WCs + Konsumer-Apps)
// rufen die helpers statt selber CustomEvent-Boilerplate zu schreiben.
//
// Pattern-Parallele: pfs-login dispatchToast(...) konsolidiert auch das
// "new CustomEvent + dispatchEvent + try/catch"-Pattern.

import type { PfsBannerDetail } from "../components/banner/pfs-banner-host.js";
import { withApiBase } from "./api-fetch.js";
import { dispatchOverlay } from "./overlay-bus.js";

export type { PfsBannerDetail };

/**
 * dispatchBanner feuert ein `pfs:banner`-Event an window. Wird von
 * <pfs-banner-host> abgefangen + gerendert. Best-effort — Errors im
 * dispatch (z.B. wenn window im Test-Setup fehlt) werden geschluckt.
 *
 * Konsumer-Beispiel — Wartungsfenster:
 *
 *   dispatchBanner({
 *     id: "maintenance-2026-05-22",
 *     title: "Geplante Wartung",
 *     message: "Heute 22:00-23:00 ist mednet kurz nicht erreichbar.",
 *     variant: "info",
 *     dismissible: true,
 *   });
 */
export function dispatchBanner(detail: PfsBannerDetail): void {
  try {
    window.dispatchEvent(
      new CustomEvent("pfs:banner", {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  } catch {
    // Best-effort: window-Dispatch fehlt im Test-Setup
  }
}

/**
 * dispatchBannerClose schließt einen Banner per id. Best-effort.
 */
export function dispatchBannerClose(id: string): void {
  try {
    window.dispatchEvent(
      new CustomEvent("pfs:banner-close", {
        detail: { id },
        bubbles: true,
        composed: true,
      }),
    );
  } catch {
    // ignore
  }
}

export interface DispatchSessionExpiredOptions {
  /** Default 10. 0 = kein Countdown. */
  countdownSeconds?: number;
  /**
   * Default: `${readSourceSync("apiBase")}/auth/logout`. Der
   * Endpoint MUSS einen Full-Logout machen (mednet-main session-clear
   * + pfs-auth SSO-Cookie-Clear) bevor der User zu /auth/initiate
   * weitergeleitet wird. Sonst macht /authorize silent re-auth gegen
   * den noch-valid pfs-auth-UI-Cookie + User landet nach Banner-
   * Countdown ohne Credentials-Eingabe wieder in der App
   * (widersprüchlich zu "Sitzung abgelaufen"). Mednet-main's
   * /auth/logout-Handler chained nach Logout direkt zu /auth/initiate
   * via return_to-param, so Credentials-Entry erzwungen.
   */
  loginUrl?: string;
  /** Default `location.pathname + location.search`. */
  returnTo?: string;
  /** PFS-580: Scrim-Stil des Overlays. "dark"|"blur"|"none". Default "blur". */
  scrim?: "dark" | "blur" | "none";
  /** PFS-580: Layout-Form. "card"|"band". Default "band" (Full-Width-Streifen). */
  layout?: "card" | "band";
}

/**
 * dispatchSessionExpiredBanner ist der spezialisierte Helper für die
 * PFS-450-401-UX. Konfiguriert pfs:banner mit dem "Sitzung abgelaufen"-
 * Pattern (Countdown + Login-Button, KEIN "Abbrechen" + KEIN X — die
 * Sitzung kann man nicht "weiterführen", User MUSS neu anmelden;
 * Pascal-Direktive 2026-05-21).
 *
 * id stabil auf "pfs-session-expired" → wenn mehrere 401-Responses
 * parallel reinkommen, replaced der Banner sich selbst statt Stack-Spam.
 *
 * Default loginUrl ist `${apiBase}/auth/logout`. `apiBase` kommt über die
 * generische Host-Schnittstelle (`readSourceSync("apiBase")`) — der Host
 * (mednet-main / WP-Embed-Adapter) provided ihn; das Framework kennt das
 * `__PFS_API_BASE__`-Global NICHT mehr. Bei WP-Embed (apiBase z.B.
 * "/member-area") landet der User in WP-Plugin's PHP-Auth-Flow; standalone
 * (kein Provider → "") geht direkt zu /auth/initiate.
 */
export function dispatchSessionExpiredOverlay(
  options: DispatchSessionExpiredOptions = {},
): void {
  const countdownSeconds = options.countdownSeconds ?? 10;
  const loginUrl = options.loginUrl ?? withApiBase("/auth/logout");
  const returnTo =
    options.returnTo ??
    (typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "/");
  const sep = loginUrl.includes("?") ? "&" : "?";
  const href = `${loginUrl}${sep}return_to=${encodeURIComponent(returnTo)}`;

  // PFS-580: Session-Ablauf rendert jetzt als App-Root-Overlay (Scrim + Card
  // über die GANZE App), nicht mehr als Top-Banner — embed-korrekt (deckt nur
  // die Embed-Region statt der WP-Seite) + unübersehbar/blockierend. Allgemeine
  // Banner (Wartung, Outage) bleiben dispatchBanner. Config identisch: Countdown
  // + Login-Button, KEIN dismissible (Sitzung nicht weiterführbar, UX-Pascal
  // 2026-05-21).
  dispatchOverlay({
    id: "pfs-session-expired",
    title: "Sitzung abgelaufen",
    message: "Bitte erneut anmelden.",
    // PFS-580: KEIN variant → Akzent = Brand-Primary (mednet blau, sparkden orange),
    // statt des semantischen Warning-Amber. Pascal-Entscheidung 2026-06-21.
    scrim: options.scrim ?? "blur",
    layout: options.layout ?? "band",
    countdownSeconds,
    primary: { label: "Jetzt anmelden", href },
    dismissible: false,
  });
}

/**
 * @deprecated Name-Alias: Session-Ablauf rendert seit PFS-580 als **Overlay**,
 * nicht als Banner. Bestehende Aufrufer (escalateIfSessionDead etc.) bleiben
 * funktionsfähig. Neuer Code: dispatchSessionExpiredOverlay.
 */
export const dispatchSessionExpiredBanner = dispatchSessionExpiredOverlay;
